import React, { useEffect, useState } from 'react';
import { XIcon, CalendarIcon, MapPinIcon, MonitorIcon, UnlockIcon, CheckSquareIcon, SquareIcon, LayoutGridIcon, ImageIcon } from 'lucide-react';
import { BookingSlot, Store, User, MediaType } from '../../data/types';
import { getStoreById } from '../../data/stores';
import { brands } from '../../data/brands';
import { Button } from '../ui/Button';
import { ArtworkUpload } from './ArtworkUpload';
const mediaTypeIcons: Record<MediaType, React.ReactNode> = {
  'Digital Outposts': <LayoutGridIcon className="w-4 h-4" data-id="element-306" />,
  'Digital Drive Aisle': <ImageIcon className="w-4 h-4" data-id="element-307" />,
  'Digital Windows': <MonitorIcon className="w-4 h-4" data-id="element-308" />
};
interface BookingPanelProps {
  slot: BookingSlot | null;
  weekSlots?: BookingSlot[];
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onConfirm: (notes?: string, brandName?: string, campaignName?: string) => void;
  onConfirmMultiple?: (slotIds: string[], notes?: string, brandName?: string, campaignName?: string) => void;
  onRemoveAvailability?: (slot: BookingSlot) => void;
  onBookInternal?: (slot: BookingSlot, notes?: string, campaignName?: string) => void;
  onUnblockToAvailable?: (slot: BookingSlot) => void;
}
export function BookingPanel({
  slot,
  weekSlots,
  isOpen,
  user,
  onClose,
  onConfirm,
  onConfirmMultiple,
  onRemoveAvailability,
  onBookInternal,
  onUnblockToAvailable
}: BookingPanelProps) {
  const [notes, setNotes] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [store, setStore] = useState<Store | null>(null);
  const [bookingMode, setBookingMode] = useState<'brand' | 'internal' | 'unblock'>('brand');
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());
  const isAdmin = user?.role === 'admin';
  const isMultiMode = !!(weekSlots && weekSlots.length > 0);
  const isBlocked = !isMultiMode && slot?.status === 'blocked';
  const isBooked = !isMultiMode && slot?.status === 'booked';
  const isInternal = !isMultiMode && slot?.status === 'internal';
  useEffect(() => {
    const targetSlot = isMultiMode ? weekSlots?.[0] : slot;
    if (targetSlot) {
      const foundStore = getStoreById(targetSlot.storeId);
      setStore(foundStore || null);
    }
  }, [slot, weekSlots, isMultiMode]);
  useEffect(() => {
    if (!isOpen) {
      setNotes('');
      setCampaignName('');
      setSelectedBrand('');
      setIsSubmitting(false);
      setBookingMode('brand');
      setSelectedSlotIds(new Set());
    }
  }, [isOpen]);
  // Auto-select all week slots when opening multi mode
  useEffect(() => {
    if (isMultiMode && weekSlots && isOpen) {
      setSelectedSlotIds(new Set(weekSlots.map(s => s.id)));
    }
  }, [isMultiMode, weekSlots, isOpen]);
  // Set default mode based on slot status (single mode only)
  useEffect(() => {
    if (!isMultiMode && slot && isAdmin) {
      if (slot.status === 'blocked') {
        setBookingMode('unblock');
      } else if (slot.status === 'booked') {
        if (slot.internalCampaignName) {
          setBookingMode('internal');
          setCampaignName(slot.internalCampaignName);
        } else {
          setBookingMode('brand');
          setSelectedBrand(slot.brandName || '');
        }
      } else if (slot.status === 'internal') {
        setBookingMode('internal');
        setCampaignName(slot.internalCampaignName || '');
      } else {
        setBookingMode('brand');
      }
    }
  }, [slot, isAdmin, isMultiMode]);
  const toggleSlotSelection = (slotId: string) => {
    setSelectedSlotIds(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) {
        next.delete(slotId);
      } else {
        next.add(slotId);
      }
      return next;
    });
  };
  const handleConfirm = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    if (isMultiMode && onConfirmMultiple) {
      const ids = Array.from(selectedSlotIds);
      if (bookingMode === 'internal') {
        onConfirmMultiple(ids, notes, undefined, campaignName || undefined);
      } else {
        onConfirmMultiple(ids, notes, selectedBrand || undefined, undefined);
      }
    } else if (bookingMode === 'unblock' && onUnblockToAvailable && slot) {
      onUnblockToAvailable(slot);
    } else if (bookingMode === 'internal' && onBookInternal && slot) {
      onBookInternal(slot, notes, campaignName || undefined);
    } else if (bookingMode === 'brand') {
      onConfirm(notes, selectedBrand || undefined, undefined);
    } else {
      onConfirm(notes);
    }
    setIsSubmitting(false);
  };
  const handleRemove = async () => {
    if (onRemoveAvailability && slot) {
      setIsSubmitting(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      onRemoveAvailability(slot);
      setIsSubmitting(false);
    }
  };
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(price);
  };
  const displaySlot = isMultiMode ? weekSlots?.[0] : slot;
  if (!displaySlot) return null;
  const panelTitle = isMultiMode ? `Book Week ${displaySlot.weekNumber}` : isBlocked && isAdmin ? 'Manage Blocked Slot' : (isBooked || isInternal) && isAdmin ? `Manage Slot — ${displaySlot.brandName || displaySlot.internalCampaignName || 'Booked'}` : bookingMode === 'internal' ? 'Woolworths Booking' : 'Book Media Slot';
  const confirmLabel = isSubmitting ? 'Processing...' : isMultiMode ? bookingMode === 'internal' ? `Book ${selectedSlotIds.size} Slot${selectedSlotIds.size !== 1 ? 's' : ''} — Woolworths` : `Book ${selectedSlotIds.size} Slot${selectedSlotIds.size !== 1 ? 's' : ''} for Brand` : bookingMode === 'unblock' ? isBooked || isInternal ? 'Release & Make Available' : 'Make Available' : bookingMode === 'internal' ? isBooked || isInternal ? 'Reassign to Woolworths' : 'Woolworths Booking' : isAdmin ? isBooked || isInternal ? 'Reassign to Brand' : 'Book for Brand' : 'Request Booking';
  const isConfirmDisabled = isSubmitting || isMultiMode && selectedSlotIds.size === 0 || bookingMode === 'internal' && !campaignName.trim() || bookingMode === 'brand' && isAdmin && !selectedBrand.trim();
  return <>
      {/* Backdrop */}
      <div className={`
          fixed inset-0 bg-black/30 z-40
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `} onClick={onClose} aria-hidden="true" data-id="element-309" />

      {/* Panel */}
      <aside className={`
          fixed right-0 top-0 h-full w-[480px] max-w-full
          bg-white shadow-2xl z-50
          flex flex-col
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `} role="dialog" aria-modal="true" aria-labelledby="booking-panel-title" data-id="element-310">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-311">
          <h2 id="booking-panel-title" className="text-lg font-semibold text-text-primary" data-id="element-312">
            {panelTitle}
          </h2>
          <button onClick={onClose} className="p-1.5 text-text-tertiary hover:text-text-primary transition-colors rounded-sm hover:bg-surface" aria-label="Close panel" data-id="element-313">
            <XIcon className="w-5 h-5" data-id="element-314" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-315">
          {/* Store Info */}
          <div className="mb-6" data-id="element-316">
            <h3 className="text-xl font-semibold text-text-primary mb-1" data-id="element-317">
              {store?.name}
            </h3>
            <div className="flex items-center gap-1.5 text-sm text-text-secondary" data-id="element-318">
              <MapPinIcon className="w-4 h-4" data-id="element-319" />
              {store?.location}, {store?.province}
            </div>
          </div>

          {/* Status Banners (single mode only) */}
          {!isMultiMode && isBlocked && isAdmin && <div className="mb-6 p-3 bg-blocked/10 border border-blocked/20 rounded-sm" data-id="element-320">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary mb-1" data-id="element-321">
                <div className="w-2 h-2 rounded-full bg-blocked" data-id="element-322" />
                This slot is currently blocked
              </div>
              <p className="text-xs text-text-secondary" data-id="element-323">
                Choose an action below to change the status of this slot.
              </p>
            </div>}
          {!isMultiMode && isBooked && isAdmin && <div className="mb-6 p-3 bg-booked/10 border border-booked/20 rounded-sm" data-id="element-324">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary mb-1" data-id="element-325">
                <div className="w-2 h-2 rounded-full bg-booked" data-id="element-326" />
                Currently booked
                {displaySlot.internalCampaignName ? `: Woolworths — ${displaySlot.internalCampaignName}` : displaySlot.brandName ? `: ${displaySlot.brandName}` : ''}
              </div>
              <p className="text-xs text-text-secondary" data-id="element-327">
                You can change the booking type or reassign this slot.
              </p>
            </div>}
          {!isMultiMode && isInternal && isAdmin && <div className="mb-6 p-3 bg-internal/10 border border-internal/20 rounded-sm" data-id="element-328">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary mb-1" data-id="element-329">
                <div className="w-2 h-2 rounded-full bg-internal" data-id="element-330" />
                Woolworths campaign
                {displaySlot.internalCampaignName ? `: ${displaySlot.internalCampaignName}` : ''}
              </div>
              <p className="text-xs text-text-secondary" data-id="element-331">
                You can change the booking type or reassign this slot.
              </p>
            </div>}

          {/* Booking Period */}
          <div className="space-y-4 mb-6" data-id="element-332">
            {!isMultiMode && <div className="flex items-start gap-3 p-3 bg-surface-warm rounded-sm" data-id="element-333">
                <MonitorIcon className="w-5 h-5 text-text-secondary mt-0.5" data-id="element-334" />
                <div data-id="element-335">
                  <div className="text-xs text-text-tertiary uppercase tracking-wide mb-0.5" data-id="element-336">
                    Media Type
                  </div>
                  <div className="text-sm font-medium text-text-primary" data-id="element-337">
                    {displaySlot.mediaType}
                  </div>
                </div>
              </div>}

            <div className="flex items-start gap-3 p-3 bg-surface-warm rounded-sm" data-id="element-338">
              <CalendarIcon className="w-5 h-5 text-text-secondary mt-0.5" data-id="element-339" />
              <div data-id="element-340">
                <div className="text-xs text-text-tertiary uppercase tracking-wide mb-0.5" data-id="element-341">
                  Booking Period
                </div>
                <div className="text-sm font-medium text-text-primary" data-id="element-342">
                  Week {displaySlot.weekNumber}
                </div>
                <div className="text-xs text-text-secondary mt-0.5" data-id="element-343">
                  {formatDate(displaySlot.weekStartDate)} —{' '}
                  {formatDate(displaySlot.weekEndDate)}
                </div>
              </div>
            </div>
          </div>

          {/* Multi-slot: Media Type Selection */}
          {isMultiMode && weekSlots && <div className="mb-6" data-id="element-344">
              <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3" data-id="element-345">
                Select Media Types
              </label>
              <div className="space-y-2" data-id="element-346">
                {weekSlots.map(ws => {
              const isSelected = selectedSlotIds.has(ws.id);
              return <button key={ws.id} onClick={() => toggleSlotSelection(ws.id)} className={`
                        w-full flex items-center gap-3 p-3 rounded-sm border transition-colors text-left
                        ${isSelected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-surface'}
                      `} data-id="element-347">
                      {isSelected ? <CheckSquareIcon className="w-5 h-5 text-primary flex-shrink-0" data-id="element-348" /> : <SquareIcon className="w-5 h-5 text-text-tertiary flex-shrink-0" data-id="element-349" />}
                      <span className="text-text-secondary flex-shrink-0" data-id="element-350">
                        {mediaTypeIcons[ws.mediaType]}
                      </span>
                      <div className="flex-1 min-w-0" data-id="element-351">
                        <div className="text-sm font-medium text-text-primary" data-id="element-352">
                          {ws.mediaType}
                        </div>
                        <div className="text-xs text-text-tertiary" data-id="element-353">
                          {ws.status === 'available' ? 'Available' : ws.status === 'blocked' ? 'Blocked' : ws.status}
                        </div>
                      </div>
                    </button>;
            })}
              </div>
              {weekSlots.length === 0 && <p className="text-sm text-text-tertiary italic" data-id="element-354">
                  No available media types for this week.
                </p>}
            </div>}

          {/* Admin Mode Selector */}
          {isAdmin && <div className="mb-6 border-b border-border pb-6" data-id="element-355">
              <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3" data-id="element-356">
                Action
              </label>
              <div className="flex gap-2" data-id="element-357">
                {!isMultiMode && (isBlocked || isBooked || isInternal) && <Button variant={bookingMode === 'unblock' ? 'primary' : 'secondary'} size="sm" onClick={() => setBookingMode('unblock')} className="flex-1" data-id="element-358">
                    {isBlocked ? 'Make Available' : 'Release Slot'}
                  </Button>}
                <Button variant={bookingMode === 'brand' ? 'primary' : 'secondary'} size="sm" onClick={() => setBookingMode('brand')} className="flex-1" data-id="element-359">
                  {!isMultiMode && (isBooked || isInternal) ? 'Reassign to Brand' : 'Brand Booking'}
                </Button>
                <Button variant={bookingMode === 'internal' ? 'primary' : 'secondary'} size="sm" onClick={() => setBookingMode('internal')} className="flex-1" data-id="element-360">
                  Woolworths Booking
                </Button>
              </div>
            </div>}

          {/* Unblock/Release info (single mode only) */}
          {!isMultiMode && bookingMode === 'unblock' && <div className="flex items-start gap-3 p-4 bg-available-bg border border-available/20 rounded-sm mb-6" data-id="element-361">
              <UnlockIcon className="w-5 h-5 text-available mt-0.5 flex-shrink-0" data-id="element-362" />
              <div data-id="element-363">
                <div className="text-sm font-medium text-text-primary mb-0.5" data-id="element-364">
                  {isBlocked ? 'Restore Availability' : 'Release Booking'}
                </div>
                <p className="text-xs text-text-secondary" data-id="element-365">
                  {isBlocked ? 'This will change the slot from blocked to available, allowing brands to book it.' : `This will release the slot${displaySlot.brandName ? ` currently held by ${displaySlot.brandName}` : displaySlot.internalCampaignName ? ` for "${displaySlot.internalCampaignName}"` : ''} and make it available for new bookings.`}
                </p>
              </div>
            </div>}

          {/* Brand Selector */}
          {bookingMode === 'brand' && isAdmin && <div className="mb-6" data-id="element-366">
              <label htmlFor="brand-select" className="block text-sm font-medium text-text-primary mb-2" data-id="element-367">
                Brand <span className="text-urgent" data-id="element-368">*</span>
              </label>
              <select id="brand-select" value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)} className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-369">
                <option value="" data-id="element-370">Select a brand...</option>
                {brands.map(brand => <option key={brand.id} value={brand.name} data-id="element-371">
                    {brand.name}
                  </option>)}
              </select>
            </div>}

          {/* Price (non-admin brand only) */}
          {!isMultiMode && bookingMode === 'brand' && !isAdmin && <div className="flex items-center justify-between py-4 border-y border-border mb-6" data-id="element-372">
              <span className="text-sm text-text-secondary" data-id="element-373">
                Rate Card Price
              </span>
              <span className="text-2xl font-bold text-text-primary" data-id="element-374">
                {formatPrice(displaySlot.price)}
              </span>
            </div>}

          {/* Campaign Name */}
          {bookingMode === 'internal' && <div className="mb-6" data-id="element-375">
              <label htmlFor="campaign-name" className="block text-sm font-medium text-text-primary mb-2" data-id="element-376">
                Woolworths Booking Name <span className="text-urgent" data-id="element-377">*</span>
              </label>
              <input id="campaign-name" type="text" value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="e.g. Summer Essentials, Back to School..." className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-378" />
              <p className="text-xs text-text-tertiary mt-1.5" data-id="element-379">
                Name the Woolworths Booking.
              </p>
            </div>}

          {/* Artwork Upload */}
          {bookingMode !== 'unblock' && <div className="mb-6" data-id="element-380">
              <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-381">
                Artwork (Optional)
              </label>
              <ArtworkUpload data-id="element-382" />
            </div>}

          {/* Notes */}
          {bookingMode !== 'unblock' && <div data-id="element-383">
              <label htmlFor="booking-notes" className="block text-sm font-medium text-text-primary mb-2" data-id="element-384">
                Notes (Optional)
              </label>
              <textarea id="booking-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any special requirements or notes..." rows={3} className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none" data-id="element-385" />
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-surface space-y-2" data-id="element-386">
          {!isMultiMode && isAdmin && bookingMode === 'brand' && !isBlocked && !isBooked && !isInternal && <Button variant="secondary" size="lg" onClick={handleRemove} disabled={isSubmitting} className="w-full mb-2 border-urgent text-urgent hover:bg-urgent/5" data-id="element-387">
                {isSubmitting ? 'Processing...' : 'Block Slot'}
              </Button>}
          <Button variant="primary" size="lg" onClick={handleConfirm} disabled={isConfirmDisabled} className="w-full" data-id="element-388">
            {confirmLabel}
          </Button>
          <Button variant="ghost" size="lg" onClick={onClose} className="w-full" data-id="element-389">
            Cancel
          </Button>
        </div>
      </aside>
    </>;
}